<template>
  <div class="musubi-page musubi-page--sources">
    <header class="musubi-page__head">
      <h1>Crawl sources</h1>
      <p class="text-muted">
        Configured lead sources. Each <strong>Run</strong> enqueues a discovery job; the cron
        ticks the queue every minute (or use <strong>Tick now</strong>). Discovered leads land in the
        triage queue scored against your signals.
      </p>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message && !newSourceId" class="alert alert-success py-2 small">{{ message }}</p>
    <div v-if="newSourceId" class="alert alert-success py-2 small d-flex align-items-center gap-2 flex-wrap">
      <span><CygnusIcon name="check-circle" class="me-1" />{{ message }}</span>
      <a class="btn btn-sm btn-link p-0 ms-1" :href="`/acquire/pipeline-wizard?source=${newSourceId}`">
        Next: define who to target <CygnusIcon name="arrow-right" class="ms-1" />
      </a>
    </div>

    <!-- Slice C — pre-run corpus-reuse prompt (spend guard for billed Places sweeps) -->
    <div v-if="reusePrompt" class="alert alert-info py-2 small d-flex align-items-center gap-2 flex-wrap reuse-prompt">
      <span>
        <CygnusIcon name="refresh" class="me-1" />
        This exact sweep completed <strong>{{ reusePrompt.when }}</strong> and found
        <strong>{{ reusePrompt.found }}</strong> — Run will reuse those results free.
        Re-fetch anyway (re-bills)?
      </span>
      <span class="ms-auto d-flex gap-2">
        <button class="btn btn-sm btn-primary" @click="doRun(reusePrompt.id, false)">Run (reuse)</button>
        <button class="btn btn-sm btn-outline-danger" @click="doRun(reusePrompt.id, true)">Force re-fetch</button>
        <button class="btn btn-sm btn-link" @click="reusePrompt = null">Cancel</button>
      </span>
    </div>

    <!-- Slice B — inline confirm for clearing a coverage plan (no native dialog) -->
    <div v-if="resetPrompt" class="alert alert-warning py-2 small d-flex align-items-center gap-2 flex-wrap reuse-prompt">
      <span>
        <CygnusIcon name="refresh" class="me-1" />
        Reset coverage for source <strong>#{{ resetPrompt.id }}</strong>? The next run re-plans and
        re-sweeps the whole area from scratch (Places re-bills).
      </span>
      <span class="ms-auto d-flex gap-2">
        <button class="btn btn-sm btn-warning" @click="doReset(resetPrompt.id)">Reset coverage</button>
        <button class="btn btn-sm btn-link" @click="resetPrompt = null">Cancel</button>
      </span>
    </div>

    <section class="actions-bar">
      <button class="btn btn-outline-primary" @click="openCreate">
        <CygnusIcon name="plus" class="me-1" />New source
      </button>
      <button class="btn btn-outline-secondary" :disabled="busy" @click="tickNow">
        <CygnusIcon name="bolt" class="me-1" />Tick now
      </button>
      <button class="btn btn-link" :disabled="loading" @click="load">
        <CygnusIcon name="refresh" /> Refresh
      </button>
      <button class="btn btn-link" @click="toggleArchived">
        <CygnusIcon :name="showArchived ? 'eye' : 'box'" class="me-1" />{{ showArchived ? 'Hide archived' : 'Show archived' }}
      </button>
      <a class="btn btn-link ms-auto" href="/acquire/pipeline-wizard" title="Set up a crawl → audience → flow pipeline in one guided flow">
        <CygnusIcon name="diagram" class="me-1" />Guided setup
      </a>
    </section>

    <section class="cs-filters">
      <input v-model="search" class="cs-input" placeholder="Search name, query or location…">
      <select v-model="typeFilter" class="cs-select">
        <option value="">Any type</option>
        <option v-for="t in presentTypes" :key="t" :value="t">{{ typeLabel(t) }}</option>
      </select>
      <button v-if="filtersActive" type="button" class="btn btn-sm btn-link p-0" @click="clearFilters">Clear</button>
      <span class="cs-count">{{ visibleRows.length }} of {{ rows.length }}</span>
    </section>

    <section v-if="showCreate" class="editor card mb-3">
      <div class="card-body">
        <h2 class="h6 mb-2">{{ editingId ? `Edit source #${editingId}` : 'New source' }}</h2>

        <!-- source type picker (create only — immutable on edit) -->
        <div v-if="!editingId" class="csrc-picker mb-3">
          <label class="form-label small text-muted d-block mb-2">Source type</label>
          <div class="csrc-tiles">
            <button
              v-for="t in sourceTypes" :key="t.value" type="button"
              class="csrc-tile"
              :class="{ 'csrc-tile--on': form.type === t.value, 'csrc-tile--locked': lockFor(t.value).locked }"
              :disabled="lockFor(t.value).locked"
              :title="lockFor(t.value).locked ? `Available on the ${lockFor(t.value).plan} plan` : ''"
              @click="selectType(t.value)"
            >
              <span class="csrc-tile__icon">
                <CygnusIcon name="lock" v-if="lockFor(t.value).locked" />
                <CygnusIcon v-else :name="t.icon" />
              </span>
              <span class="csrc-tile__name">{{ t.label }}</span>
              <span v-if="lockFor(t.value).locked" class="csrc-tile__lock">Available on {{ lockFor(t.value).plan || 'a higher plan' }}</span>
              <span v-else class="csrc-tile__desc">{{ t.desc }}</span>
            </button>
          </div>
        </div>
        <div v-else class="csrc-typebadge mb-3">
          <CygnusIcon :name="currentTypeMeta.icon" />
          <span>{{ currentTypeMeta.label }}</span>
          <span class="form-text small ms-1 mb-0">— type can't be changed, recreate to switch.</span>
        </div>

        <form @submit.prevent="submitForm">
          <div class="mb-2">
            <label class="form-label small text-muted">Name</label>
            <input v-model="form.name" class="form-control form-control-sm" placeholder="HN: marketing automation" required>
          </div>

          <div class="mb-2" style="max-width:260px">
            <label class="form-label small text-muted">Schedule</label>
            <select v-model="form.schedule" class="form-select form-select-sm">
              <option v-for="s in scheduleOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
            </select>
            <p v-if="form.schedule !== 'manual'" class="form-text small text-muted mb-0">
              Automatic re-runs re-fetch from the provider — Places sweeps re-bill each run.
            </p>
          </div>

          <div class="csrc-fields">
            <!-- hackernews -->
            <template v-if="form.type === 'hackernews'">
              <div class="row g-2">
                <div class="col-md-6">
                  <label class="form-label small text-muted">Query <span class="text-danger">*</span></label>
                  <input v-model="form.query" class="form-control form-control-sm" placeholder="marketing automation" required>
                </div>
                <div class="col-md-3">
                  <label class="form-label small text-muted">Tags</label>
                  <select v-model="form.tags" class="form-select form-select-sm">
                    <option value="story">story</option>
                    <option value="comment">comment</option>
                    <option value="poll">poll</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label small text-muted">Hits per page</label>
                  <input v-model.number="form.hitsPerPage" type="number" min="1" class="form-control form-control-sm">
                </div>
              </div>
            </template>

            <!-- reddit -->
            <template v-else-if="form.type === 'reddit'">
              <div class="row g-2">
                <div class="col-md-5">
                  <label class="form-label small text-muted">Query <span class="text-danger">*</span></label>
                  <input v-model="form.query" class="form-control form-control-sm" placeholder="marketing automation" required>
                </div>
                <div class="col-md-4">
                  <label class="form-label small text-muted">Subreddit</label>
                  <input v-model="form.subreddit" class="form-control form-control-sm" placeholder="e.g. webdev (blank = global)">
                </div>
                <div class="col-md-3">
                  <label class="form-label small text-muted">Limit</label>
                  <input v-model.number="form.limit" type="number" min="1" class="form-control form-control-sm">
                </div>
              </div>
              <div class="mt-2">
                <label class="form-label small text-muted">User agent <small>— advanced, optional</small></label>
                <input v-model="form.user_agent" class="form-control form-control-sm" placeholder="e.g. musubi-crawler/1.0">
              </div>
            </template>

            <!-- linkedin -->
            <template v-else-if="form.type === 'linkedin'">
              <p class="form-text small mb-2"><CygnusIcon name="info-circle" class="me-1" />Runs on the browser worker tier.</p>
              <div class="row g-2">
                <div class="col-md-6">
                  <label class="form-label small text-muted">Query <span class="text-danger">*</span></label>
                  <input v-model="form.query" class="form-control form-control-sm" placeholder="need help with marketing automation" required>
                </div>
                <div class="col-md-3">
                  <label class="form-label small text-muted">Limit</label>
                  <input v-model.number="form.limit" type="number" min="1" class="form-control form-control-sm">
                </div>
                <div class="col-md-3 d-flex align-items-end">
                  <div class="form-check form-switch mb-1">
                    <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="csrc-li-enrich">
                    <label class="form-check-label small" for="csrc-li-enrich">Auto-enrich</label>
                  </div>
                </div>
              </div>
            </template>

            <!-- site_crawl -->
            <template v-else-if="form.type === 'site_crawl'">
              <div class="mb-2">
                <label class="form-label small text-muted">Seed URLs <span class="text-danger">*</span> <small class="text-muted">— one per line</small></label>
                <textarea v-model="form.seed_urls" rows="4" class="form-control form-control-sm font-monospace" placeholder="https://acme-agency.co.uk&#10;https://another-firm.com" required></textarea>
              </div>
              <div class="row g-2">
                <div class="col-md-3">
                  <label class="form-label small text-muted">Max</label>
                  <input v-model.number="form.max" type="number" min="1" class="form-control form-control-sm">
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <div class="form-check form-switch mb-1">
                    <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="csrc-sc-enrich">
                    <label class="form-check-label small" for="csrc-sc-enrich">Auto-enrich</label>
                  </div>
                </div>
              </div>
            </template>

            <!-- local_search -->
            <template v-else-if="form.type === 'local_search'">
              <div class="row g-2 align-items-end">
                <div class="col-md-8" v-if="!form.useCoords">
                  <label class="form-label small text-muted">Location <span class="text-danger">*</span></label>
                  <input v-model="form.location" class="form-control form-control-sm" placeholder="Manchester, UK">
                </div>
                <div class="col-12" v-if="form.useCoords">
                  <div class="row g-2">
                    <div class="col-md-4">
                      <label class="form-label small text-muted">Latitude</label>
                      <input v-model.number="form.lat" type="number" step="any" class="form-control form-control-sm">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small text-muted">Longitude</label>
                      <input v-model.number="form.lon" type="number" step="any" class="form-control form-control-sm">
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <button type="button" class="btn btn-link btn-sm p-0" @click="form.useCoords = !form.useCoords">
                    {{ form.useCoords ? '← Use a place name instead' : 'Use coordinates instead' }}
                  </button>
                </div>
              </div>

              <div class="csrc-field mt-2">
                <label class="form-label small text-muted">Search radius</label>
                <div class="csrc-slider">
                  <input type="range" min="1" max="50" step="1" v-model.number="form.radius_miles" class="form-range">
                  <span class="csrc-slider__val">{{ form.radius_miles }} mi</span>
                </div>
              </div>

              <div class="csrc-field mt-2">
                <label class="form-label small text-muted">Business categories</label>
                <div class="csrc-chips">
                  <button
                    v-for="c in categoryOptions" :key="c" type="button"
                    class="csrc-chip" :class="{ 'csrc-chip--on': form.categories.includes(c) }"
                    @click="toggleCategory(c)"
                  >{{ c }}</button>
                </div>
              </div>

              <div class="row g-2 mt-1">
                <div class="col-md-3">
                  <label class="form-label small text-muted">Max results</label>
                  <input v-model.number="form.max" type="number" min="1" class="form-control form-control-sm">
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <div class="form-check form-switch mb-1">
                    <input v-model="form.include_no_website" class="form-check-input" type="checkbox" role="switch" id="csrc-ls-nowebsite">
                    <label class="form-check-label small" for="csrc-ls-nowebsite">Include no-website firms</label>
                  </div>
                </div>
                <div class="col-md-3 d-flex align-items-end">
                  <div class="form-check form-switch mb-1">
                    <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="csrc-ls-enrich">
                    <label class="form-check-label small" for="csrc-ls-enrich">Auto-enrich</label>
                  </div>
                </div>
              </div>

              <div class="mt-2">
                <label class="form-label small text-muted">Overpass URL <small>— advanced, optional</small></label>
                <input v-model="form.overpass_url" class="form-control form-control-sm" placeholder="https://overpass-api.de/api/interpreter">
              </div>
            </template>

            <!-- google_places -->
            <template v-else-if="form.type === 'google_places'">
              <div class="row g-2 align-items-end">
                <div class="col-md-4">
                  <label class="form-label small text-muted">Query</label>
                  <input v-model="form.query" class="form-control form-control-sm" placeholder="businesses">
                </div>
                <div class="col-md-8" v-if="!form.useCoords">
                  <label class="form-label small text-muted">Location <span class="text-danger">*</span></label>
                  <input v-model="form.location" class="form-control form-control-sm" placeholder="Hebden Bridge, UK">
                </div>
                <div class="col-12" v-if="form.useCoords">
                  <div class="row g-2">
                    <div class="col-md-4">
                      <label class="form-label small text-muted">Latitude</label>
                      <input v-model.number="form.lat" type="number" step="any" class="form-control form-control-sm">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small text-muted">Longitude</label>
                      <input v-model.number="form.lon" type="number" step="any" class="form-control form-control-sm">
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <button type="button" class="btn btn-link btn-sm p-0" @click="form.useCoords = !form.useCoords">
                    {{ form.useCoords ? '← Use a place name instead' : 'Use coordinates instead' }}
                  </button>
                </div>
              </div>

              <div class="csrc-field mt-2">
                <label class="form-label small text-muted">Search radius</label>
                <div class="csrc-slider">
                  <input type="range" min="1" max="31" step="1" v-model.number="form.radius_miles" class="form-range">
                  <span class="csrc-slider__val">{{ form.radius_miles }} mi</span>
                </div>
              </div>

              <div class="row g-2 mt-1">
                <div class="col-md-3">
                  <label class="form-label small text-muted">Max results <small>(cap 60)</small></label>
                  <input v-model.number="form.max" type="number" min="1" max="60" class="form-control form-control-sm" @change="clampGooglePlacesMax">
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <div class="form-check form-switch mb-1">
                    <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="csrc-gp-enrich">
                    <label class="form-check-label small" for="csrc-gp-enrich">Auto-enrich</label>
                  </div>
                </div>
              </div>
              <p class="form-text small text-muted mb-0">Billed per request — needs a Places API key in Settings ▸ Enrichment. Dedupes against OSM by website.</p>
            </template>

            <!-- geo_osm (CN-1 — Canal Nav waterway sweep) -->
            <template v-else-if="form.type === 'geo_osm'">
              <p class="form-text small mb-2">
                <CygnusIcon name="droplet" class="me-1" />Sweeps a per-category radius around Canal Nav's waterway seed
                (anchors on edge/bridge/facility points — never town centroids). Resumable across runs; free OSM/Overpass.
              </p>
              <div class="row g-2">
                <div class="col-md-4">
                  <label class="form-label small text-muted">Default radius (m)</label>
                  <input v-model.number="form.radius_default_m" type="number" min="10" step="10" class="form-control form-control-sm">
                </div>
                <div class="col-md-4">
                  <label class="form-label small text-muted">Tiles per run</label>
                  <input v-model.number="form.max_tiles_per_run" type="number" min="1" class="form-control form-control-sm">
                </div>
                <div class="col-md-4 d-flex align-items-end">
                  <div class="form-check form-switch mb-1">
                    <input v-model="form.auto_enrich" class="form-check-input" type="checkbox" role="switch" id="csrc-go-enrich">
                    <label class="form-check-label small" for="csrc-go-enrich">Auto-enrich</label>
                  </div>
                </div>
              </div>

              <div class="csrc-field mt-2">
                <label class="form-label small text-muted">Categories to keep <small>— all when none picked</small></label>
                <div class="csrc-chips">
                  <button
                    v-for="c in cnCategoryOptions" :key="c" type="button"
                    class="csrc-chip" :class="{ 'csrc-chip--on': form.cn_categories.includes(c) }"
                    @click="toggleCnCategory(c)"
                  >{{ c }}</button>
                </div>
              </div>

              <div class="csrc-field mt-2">
                <label class="form-label small text-muted">Per-category radius (m) <small>— overrides the default; pubs tight, marinas wider</small></label>
                <div class="csrc-radii">
                  <div v-for="c in radiusCatKeys" :key="c" class="csrc-radius">
                    <span class="csrc-radius__label">{{ c }}</span>
                    <input v-model.number="form.radius_by_category[c]" type="number" min="10" step="10" class="form-control form-control-sm">
                  </div>
                </div>
              </div>

              <div class="mt-2">
                <label class="form-label small text-muted">Overpass endpoint <small>— advanced, optional (mirror)</small></label>
                <input v-model="form.overpass_endpoint" class="form-control form-control-sm" placeholder="https://overpass-api.de/api/interpreter">
              </div>
            </template>

            <!-- seed -->
            <template v-else-if="form.type === 'seed'">
              <p class="form-text small text-muted mb-0">No configuration — emits fixed test candidates.</p>
            </template>
          </div>

          <!-- advanced: rate limit + raw JSON (editable — power-user keys round-trip via extraConfig) -->
          <details class="csrc-adv mt-3">
            <summary>Advanced{{ form.type === 'seed' ? ' (JSON)' : '' }}</summary>
            <div class="csrc-adv__body">
              <div v-if="form.type !== 'seed'" class="mb-2" style="max-width:220px">
                <label class="form-label small text-muted">Max fetches/min</label>
                <input v-model.number="form.rate_per_min" type="number" min="1" class="form-control form-control-sm">
              </div>
              <label class="form-label small text-muted">Config (JSON) <small>— kept in sync with the form above; edit to pass through power-user keys</small></label>
              <textarea
                class="form-control form-control-sm font-monospace csrc-adv__json"
                rows="8"
                v-model="advancedText"
                @input="advancedDirty = true"
                @blur="onAdvancedBlur"
              ></textarea>
              <div v-if="advancedError" class="alert alert-warning py-1 px-2 small mt-1 mb-0">{{ advancedError }}</div>
            </div>
          </details>

          <!-- estimated reach sidecar -->
          <div v-if="form.type === 'local_search' || form.type === 'google_places'" class="coverage-box mt-2 mb-2">
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="coverageBusy" @click="checkCoverage">
              <CygnusIcon name="shield" class="me-1" />{{ coverageBusy ? 'Checking…' : 'Check reach' }}
            </button>
            <span class="form-text small ms-2">
              {{ form.type === 'google_places'
                ? 'Free OSM count vs a one-page Places probe — is a billed Places call worth it for this area?'
                : 'Estimated coverage from OpenStreetMap for this area.' }}
            </span>
            <div v-if="coverageError" class="alert alert-warning py-1 px-2 small mt-2 mb-0">{{ coverageError }}</div>
            <div v-if="coverage" class="coverage-result mt-2" :class="coverage.recommend_places ? 'coverage-result--yes' : 'coverage-result--no'">
              <div class="coverage-verdict">
                <CygnusIcon :name="coverage.recommend_places ? 'check-circle' : 'minus'" />
                {{ coverage.recommend_places ? 'Places likely worth it' : 'Places may add little' }}
              </div>
              <div class="coverage-stats small">
                <span><strong>OSM</strong> {{ coverage.osm.with_website }} with-site</span>
                <span><strong>Places</strong> {{ coverage.places.available ? (coverage.places.first_page + (coverage.places.has_more ? '+' : '')) : 'no key' }} for “{{ coverage.query }}”</span>
                <span class="text-muted">{{ coverage.radius_miles }}mi · {{ coverage.location || `${coverage.lat}, ${coverage.lon}` }}</span>
              </div>
              <div class="coverage-reason small text-muted">{{ coverage.reason }}</div>
            </div>
          </div>

          <button type="submit" class="btn btn-primary btn-sm" :disabled="busy || !canSubmit">
            {{ editingId ? 'Save changes' : 'Create source' }}
          </button>
          <button type="button" class="btn btn-link btn-sm" @click="cancelEdit">Cancel</button>
        </form>
      </div>
    </section>

    <MusubiTabulator
      :rows="visibleRows"
      :columns="columns"
      storage-key="admin.crawl-sources.v2"
      :initial-sort="{ column: 'created', dir: 'desc' }"
      :page-size="25"
      :loading="loading"
      :error="error"
    />

    <section class="crawl-activity">
      <div class="crawl-activity__head">
        <h2>Crawl activity</h2>
        <span class="crawl-count crawl-count--queued">Queued {{ jobCounts.queued || 0 }}</span>
        <span class="crawl-count crawl-count--leased">Running {{ jobCounts.leased || 0 }}</span>
        <span class="crawl-count crawl-count--done">Done {{ jobCounts.done || 0 }}</span>
        <span class="crawl-count crawl-count--failed">Failed {{ jobCounts.failed || 0 }}</span>
        <button class="btn btn-link btn-sm" @click="loadJobs"><CygnusIcon name="refresh" /> Refresh</button>
      </div>
      <p class="text-muted small">Each run enqueues a job; the cron drains the queue every minute (or hit "Tick now"). discover = find leads · enrich = fill details.</p>
      <MusubiTabulator
        :rows="jobs"
        :columns="jobColumns"
        storage-key="admin.crawl-jobs.v2"
        :initial-sort="{ column: 'id', dir: 'desc' }"
        :page-size="25"
      />
    </section>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import { esc } from "./util/cellHtml";
// Typed crawl-source form helpers — shared with the guided PipelineWizard so a
// source built there shapes its config identically to one built here.
import {
  SOURCE_TYPES, CATEGORY_OPTIONS, CN_CATEGORY_OPTIONS, SCHEDULE_OPTIONS, numOr,
  defaultFormForType, buildTypedConfig, applyConfigToForm, splitExtra, crawlTypeLock,
} from "./util/crawlSourceForm";

export default {
  name: 'AdminCrawlSources',
  components: { MusubiTabulator },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      // Client-side list filters + the archived view. The endpoint returns the
      // whole tenant's sources in one go, so filtering here costs nothing.
      search: '',
      typeFilter: '',
      showArchived: false,
      loading: false,
      busy: false,
      error: null,
      message: '',
      showCreate: false,
      editingId: null,
      newSourceId: null,
      sourceTypes: SOURCE_TYPES,
      categoryOptions: CATEGORY_OPTIONS,
      cnCategoryOptions: CN_CATEGORY_OPTIONS,
      scheduleOptions: SCHEDULE_OPTIONS,
      form: this.defaultFormForType('hackernews'),
      extraConfig: {},
      advancedText: '{}',
      advancedDirty: false,
      advancedError: null,
      jobs: [],
      jobCounts: {},
      coverage: null,
      coverageBusy: false,
      coverageError: null,
      // Slice C — pre-run reuse prompt state, keyed by source id.
      reusePrompt: null, // { id, when, found } when an inline "reuse vs re-fetch" choice is pending
      reuseBusy: false,
      // Slice B — inline "reset coverage" confirm, keyed by source id.
      resetPrompt: null, // { id } when a reset confirm is pending
      // Slice 5 §3 — per-plan crawl-type gate map (from /me/usage); disabled types
      // render as LOCKED tiles. Operators on the internal plan see all unlocked.
      crawlTypes: {},
    };
  },
  computed: {
    currentTypeMeta() {
      return this.sourceTypes.find((t) => t.value === this.form.type) || this.sourceTypes[0];
    },
    configFromForm() {
      const typed = this.buildTypedConfig(this.form);
      return { ...this.extraConfig, ...typed };
    },
    radiusCatKeys() {
      return Object.keys(this.form.radius_by_category || {});
    },
    canSubmit() {
      if (!this.form.name || !this.form.name.trim()) return false;
      const t = this.form.type;
      if (t === 'hackernews' || t === 'reddit' || t === 'linkedin') {
        return !!(this.form.query && this.form.query.trim());
      }
      if (t === 'site_crawl') return !!(this.form.seed_urls && this.form.seed_urls.trim());
      if (t === 'local_search' || t === 'google_places') {
        if (this.form.useCoords) return this.form.lat !== '' && this.form.lat != null && this.form.lon !== '' && this.form.lon != null;
        return !!(this.form.location && this.form.location.trim());
      }
      return true; // seed
    },
    /** Types actually present, so the dropdown never offers an empty result. */
    presentTypes() {
      return [...new Set(this.rows.map((r) => r.type).filter(Boolean))].sort();
    },
    filtersActive() { return !!(this.search.trim() || this.typeFilter); },
    visibleRows() {
      const q = this.search.trim().toLowerCase();
      return this.rows.filter((r) => {
        if (this.typeFilter && r.type !== this.typeFilter) return false;
        if (!q) return true;
        const c = r.config || {};
        return [r.name, this.typeLabel(r.type), c.location, c.query, c.location_hint]
          .some((f) => String(f || '').toLowerCase().includes(q));
      });
    },
    columns() {
      return [
        { key: 'id',          label: 'ID',     width: 60,  sorter: 'number' },
        { key: 'type',        label: 'Type',   width: 110 },
        { key: 'name',        label: 'Name' },
        { key: 'config', label: 'Query / location',
          format: (v) => {
            const parts = [];
            if (v && v.query) parts.push(esc(v.query));
            const loc = v && (v.location || v.location_hint);
            if (loc) parts.push(`<span class="cs-loc">${esc(loc)}</span>`);
            return parts.length ? parts.join(' ') : '—';
          } },
        { key: 'active',      label: 'Active', width: 90, hozAlign: 'center',
          format: (v, row) => row.archived_at
            ? '<span class="cs-arch-badge">archived</span>'
            : (v ? '✓' : '—') },
        { key: 'schedule',    label: 'Schedule', width: 150, sorter: 'string',
          format: (v, row) => {
            const s = v || 'manual';
            if (s === 'manual') return '<span class="text-muted">Manual</span>';
            const label = s.charAt(0).toUpperCase() + s.slice(1);
            const next = row.next_run_at ? `<span class="sched-next">next ${esc(new Date(row.next_run_at * 1000).toLocaleString())}</span>` : '';
            return `<span class="sched-badge">${esc(label)}</span>${next}`;
          } },
        { key: 'last_run_at', label: 'Last run', width: 140, sorter: 'number',
          format: (v) => v ? new Date(v * 1000).toLocaleString() : '—' },
        // Slice B — coverage-plan progress for google_places sources.
        { key: 'coverage', label: 'Coverage', width: 140, sorter: false, headerFilter: false,
          format: (v) => {
            if (!v || !v.total) return '<span class="text-muted">—</span>';
            const badge = v.complete
              ? '<span class="cov-badge cov-badge--done">complete</span>'
              : '<span class="cov-badge">in&nbsp;progress</span>';
            return `<span class="cov-progress">${esc(v.covered)}/${esc(v.total)} areas</span> ${badge}`;
          } },
        { key: 'actions', label: 'Actions', width: 250, sorter: false, headerFilter: false,
          format: (v, row) => `<button class="btn btn-sm btn-link p-0 me-3 sources-act" data-id="${esc(row.id)}" data-action="run">Run now</button>`
            + `<button class="btn btn-sm btn-link p-0 me-3 sources-act" data-id="${esc(row.id)}" data-action="edit">Edit</button>`
            + (row.coverage ? `<button class="btn btn-sm btn-link p-0 me-3 sources-act" data-id="${esc(row.id)}" data-action="reset-coverage">Reset coverage</button>` : '')
            + (row.archived_at
              ? `<button class="btn btn-sm btn-link p-0 sources-act" data-id="${esc(row.id)}" data-action="unarchive">Restore</button>`
              : `<button class="btn btn-sm btn-link p-0 text-danger sources-act" data-id="${esc(row.id)}" data-action="archive">Archive</button>`) },
      ];
    },
    jobColumns() {
      const fmtTime = (v) => v ? new Date(v * 1000).toLocaleString() : '—';
      return [
        { key: 'id',       label: 'Job', width: 70, sorter: 'number' },
        { key: 'source_id',label: 'Source', width: 150, format: (v) => v ? esc(this.sourceName(v)) : '—' },
        { key: 'type',     label: 'Type', width: 90 },
        { key: 'runtime',  label: 'Runtime', width: 90 },
        { key: 'status',   label: 'Status', width: 90,
          format: (v) => `<span class="job-st job-st--${esc(v)}">${esc(v)}</span>` },
        { key: 'found',    label: 'Found', width: 70, hozAlign: 'center', sorter: 'number',
          format: (v) => v == null ? '—' : esc(v) },
        { key: 'accepted', label: 'New', width: 65, hozAlign: 'center', sorter: 'number',
          format: (v) => v == null ? '—' : `<span class="job-new">${esc(v)}</span>` },
        { key: 'ignored',  label: 'Ignored', width: 80, hozAlign: 'center', sorter: 'number',
          format: (v) => v == null ? '—' : esc(v) },
        { key: 'attempts', label: 'Tries', width: 70, hozAlign: 'center',
          format: (v, row) => `${esc(v)}/${esc(row.max_attempts)}` },
        { key: 'created',  label: 'Created', width: 150, sorter: 'number', format: fmtTime },
        { key: 'last_error', label: 'Last error', format: (v) => v ? `<span class="text-danger">${esc(v)}</span>` : '—' },
      ];
    },
  },
  watch: {
    configFromForm: {
      immediate: true,
      handler(val) {
        if (!this.advancedDirty) this.advancedText = JSON.stringify(val, null, 2);
      },
    },
  },
  mounted() {
    this.load();
    this.loadJobs();
    this.loadCrawlTypes();
    document.addEventListener('click', this.onDelegatedClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDelegatedClick);
  },
  methods: {
    // ---- typed-form <-> config plumbing (delegated to the shared module) ----
    defaultFormForType(type) { return defaultFormForType(type); },
    buildTypedConfig(form) { return buildTypedConfig(form); },
    applyConfigToForm(type, config) { return applyConfigToForm(type, config); },
    splitExtra(type, config) { return splitExtra(type, config); },
    onAdvancedBlur() {
      this.advancedError = null;
      let parsed;
      try {
        parsed = JSON.parse(this.advancedText || '{}');
      } catch (e) {
        this.advancedError = 'Invalid JSON — fix it, or these edits are ignored on save.';
        return;
      }
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        this.advancedError = 'Config must be a JSON object.';
        return;
      }
      const type = this.form.type;
      const preservedName = this.form.name;
      this.form = this.applyConfigToForm(type, parsed);
      this.form.name = preservedName;
      this.extraConfig = this.splitExtra(type, parsed);
      this.advancedDirty = false;
    },
    // ---- type / category / field helpers ----
    lockFor(type) {
      return crawlTypeLock(this.crawlTypes, type);
    },
    async loadCrawlTypes() {
      try {
        const res = await this.api.get('/api/v1/me/usage');
        const p = res?.data ?? res;
        this.crawlTypes = (p && p.crawl_types) || {};
      } catch (e) { /* fail-open — no locks shown if usage can't be read */ }
    },
    selectType(type) {
      if (this.lockFor(type).locked) return; // non-selectable while plan-locked
      const preservedName = this.form.name;
      this.form = this.defaultFormForType(type);
      this.form.name = preservedName;
      this.extraConfig = {};
      this.advancedDirty = false;
      this.coverage = null; this.coverageError = null;
    },
    toggleCategory(c) {
      const i = this.form.categories.indexOf(c);
      if (i === -1) this.form.categories.push(c);
      else this.form.categories.splice(i, 1);
    },
    clampGooglePlacesMax() {
      const n = numOr(this.form.max, 40);
      this.form.max = Math.min(Math.max(n, 1), 60);
    },
    toggleCnCategory(c) {
      const i = this.form.cn_categories.indexOf(c);
      if (i === -1) this.form.cn_categories.push(c);
      else this.form.cn_categories.splice(i, 1);
    },
    // ---- data loading ----
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/crawl/sources' + (this.showArchived ? '?archived=1' : ''));
        const payload = res?.data?.data ?? res?.data ?? res;
        let rows = Array.isArray(payload) ? payload : (payload?.rows || []);
        // ?archived=1 returns live AND archived; narrow to archived only so the
        // toggle reads as a filter rather than an "everything" dump (same
        // convention as the drafts list).
        if (this.showArchived) rows = rows.filter((r) => r.archived_at);
        this.rows = rows;
      } catch (e) { this.error = e?.message || 'Failed to load sources.'; }
      this.loading = false;
    },
    async loadJobs() {
      try {
        const res = await this.api.get('/api/v1/admin/crawl/jobs');
        const p = res?.data ?? res;
        this.jobs = p.jobs || [];
        this.jobCounts = p.counts || {};
      } catch (e) { /* non-fatal */ }
    },
    sourceName(id) {
      const s = this.rows.find((r) => r.id === id);
      return s ? `${s.name}` : `#${id}`;
    },
    // ---- create / edit form lifecycle ----
    openCreate() {
      if (this.showCreate && !this.editingId) { this.showCreate = false; return; }
      this.editingId = null;
      this.form = this.defaultFormForType('hackernews');
      this.extraConfig = {};
      this.advancedDirty = false;
      this.coverage = null; this.coverageError = null;
      this.showCreate = true;
    },
    cancelEdit() {
      this.showCreate = false;
      this.editingId = null;
      this.form = this.defaultFormForType('hackernews');
      this.extraConfig = {};
      this.advancedDirty = false;
      this.coverage = null; this.coverageError = null;
    },
    startEdit(id) {
      const s = this.rows.find((r) => r.id === id);
      if (!s) return;
      this.editingId = id;
      const type = s.type;
      const config = s.config || {};
      this.form = this.applyConfigToForm(type, config);
      this.form.name = s.name;
      this.extraConfig = this.splitExtra(type, config);
      this.advancedDirty = false;
      this.showCreate = true;
      this.error = null; this.message = '';
      this.coverage = null; this.coverageError = null;
    },
    // ---- reach estimate ----
    async checkCoverage() {
      this.coverageError = null; this.coverage = null;
      const config = this.configFromForm;
      const hasLoc = (config.location && String(config.location).trim() !== '')
        || (config.lat != null && config.lon != null);
      if (!hasLoc) { this.coverageError = 'Add a location (or lat/lon) first.'; return; }
      this.coverageBusy = true;
      try {
        const params = new URLSearchParams();
        if (config.location) params.set('location', config.location);
        if (config.lat != null) params.set('lat', config.lat);
        if (config.lon != null) params.set('lon', config.lon);
        if (config.query) params.set('query', config.query);
        if (config.radius_miles != null) params.set('radius_miles', config.radius_miles);
        if (Array.isArray(config.categories) && config.categories.length) params.set('categories', config.categories.join(','));
        const res = await this.api.get(`/api/v1/admin/crawl/coverage?${params.toString()}`);
        const p = res?.data ?? res;
        if (p && p.error) this.coverageError = p.error;
        else this.coverage = p;
      } catch (e) {
        this.coverageError = e?.response?.data?.error || e?.message || 'Coverage check failed.';
      }
      this.coverageBusy = false;
    },
    // ---- submit ----
    submitForm() {
      return this.editingId ? this.update() : this.create();
    },
    async create() {
      this.busy = true; this.error = null; this.message = ''; this.newSourceId = null;
      const config = this.configFromForm;
      try {
        const res = await this.api.post('/api/v1/admin/crawl/sources', { type: this.form.type, name: this.form.name, config });
        const p = res?.data ?? res;
        this.newSourceId = p?.id ?? p?.data?.id ?? null;
        this.message = 'Source created.';
        this.cancelEdit();
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Create failed.'; }
      this.busy = false;
    },
    async update() {
      this.busy = true; this.error = null; this.message = '';
      const config = this.configFromForm;
      const id = this.editingId;
      try {
        await this.api.patch(`/api/v1/admin/crawl/sources/${id}`, { name: this.form.name, config });
        this.message = `Source #${id} updated.`;
        this.cancelEdit();
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Update failed.'; }
      this.busy = false;
    },
    onDelegatedClick(ev) {
      const btn = ev.target.closest('.sources-act');
      if (!btn) return;
      ev.preventDefault(); ev.stopPropagation();
      const id = Number(btn.dataset.id);
      if (!id) return;
      if (btn.dataset.action === 'archive') this.archive(id);
      else if (btn.dataset.action === 'unarchive') this.unarchive(id);
      else if (btn.dataset.action === 'edit') this.startEdit(id);
      else if (btn.dataset.action === 'reset-coverage') this.resetPrompt = { id };
      else this.run(id);
    },
    // Reusable source types where a pre-run corpus-reuse check is worthwhile —
    // google_places is BILLED, so reuse there is a spend guard; local_search is
    // free but reuse still skips a redundant crawl.
    isReusableType(type) {
      return type === 'google_places' || type === 'local_search';
    },
    async run(id) {
      this.error = null; this.message = ''; this.reusePrompt = null;
      const src = this.rows.find((r) => r.id === id);
      // For reusable types, probe the corpus first — if this exact sweep is
      // still cached, offer to reuse it free rather than silently re-billing.
      if (src && this.isReusableType(src.type)) {
        this.reuseBusy = true;
        try {
          const res = await this.api.get(`/api/v1/admin/crawl/sources/${id}/reuse-check`);
          const p = res?.data ?? res;
          if (p && p.reusable) {
            this.reusePrompt = {
              id,
              when: p.last_completed_at ? new Date(p.last_completed_at * 1000).toLocaleString() : 'recently',
              found: p.found ?? 0,
            };
            this.reuseBusy = false;
            return; // wait for the operator's Reuse / Force choice
          }
        } catch (e) { /* probe is best-effort — fall through to a normal run */ }
        this.reuseBusy = false;
      }
      await this.doRun(id, false);
    },
    async doRun(id, force) {
      this.error = null; this.reusePrompt = null;
      try {
        await this.api.post(`/api/v1/admin/crawl/sources/${id}/run`, { force: !!force });
        this.message = force
          ? `Source #${id} queued (force re-fetch) — re-fetches live on the next tick.`
          : `Source #${id} queued — runs on the next cron tick (or hit "Tick now").`;
        await this.loadJobs();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Run failed.'; }
    },
    /**
     * Archive, not delete. The API keeps the row - it is the provenance of every
     * prospect the source found - so this is reversible and nothing is lost.
     */
    async archive(id) {
      if (!window.confirm(`Archive source #${id}? It stops running and leaves the list, but nothing it found is deleted. You can restore it any time.`)) return;
      this.error = null; this.message = '';
      try {
        await this.api.delete(`/api/v1/admin/crawl/sources/${id}`);
        this.message = `Source #${id} archived. Use "Show archived" to bring it back.`;
        await this.load();
        await this.loadJobs();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Archive failed.'; }
    },
    async unarchive(id) {
      this.error = null; this.message = '';
      try {
        await this.api.post(`/api/v1/admin/crawl/sources/${id}/unarchive`, {});
        this.message = `Source #${id} restored. It stays switched off until you activate it.`;
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Restore failed.'; }
    },
    toggleArchived() { this.showArchived = !this.showArchived; this.load(); },
    clearFilters() { this.search = ''; this.typeFilter = ''; },
    /** Slug -> the catalogue's human label (falls back to the slug). */
    typeLabel(type) {
      return (SOURCE_TYPES.find((t) => t.value === type) || {}).label || type || '—';
    },
    async doReset(id) {
      this.error = null; this.message = ''; this.resetPrompt = null;
      try {
        await this.api.post(`/api/v1/admin/crawl/sources/${id}/reset-state`, {});
        this.message = `Coverage for source #${id} reset — the next run re-sweeps the whole area.`;
        await this.load();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Reset failed.'; }
    },
    async tickNow() {
      this.busy = true; this.error = null; this.message = '';
      try {
        const res = await this.api.post('/api/v1/admin/crawl/tick', {});
        const c = (res?.data ?? res)?.counters || {};
        this.message = `Ticked: ${c.jobs || 0} job(s), ${c.candidates || 0} candidate(s), ${c.persisted || 0} new/updated lead(s).`;
        await this.load();
        await this.loadJobs();
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Tick failed.'; }
      this.busy = false;
    },
  },
};
</script>

<style>
.musubi-page--sources { padding: var(--m-space-6, 32px); }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.cs-filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.cs-input, .cs-select {
  border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 6px;
  font-size: 0.82rem; padding: 6px 10px; color: var(--m-text-primary); font-family: inherit;
}
.cs-input { min-width: 240px; }
.cs-count { margin-left: auto; font-size: 0.75rem; color: var(--m-text-tertiary); }
.cs-arch-badge { font-size: 0.68rem; font-weight: 650; color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 999px; padding: 1px 8px; }
.cs-loc { color: var(--m-text-tertiary); font-size: 0.78rem; }
.editor { background: var(--m-bg-card, #FFF); }
.crawl-activity { margin-top: 28px; }
.crawl-activity__head { display: flex; align-items: center; gap: 10px; }
.crawl-activity__head h2 { font-size: 1.1rem; font-weight: 600; margin: 0 8px 0 0; }
.crawl-count { font-size: 0.74rem; font-weight: 600; padding: 2px 9px; border-radius: 999px; border: 1px solid var(--m-border, #E2E8F0); }
.crawl-count--queued { color: var(--m-status-warning, #b45309); }
.crawl-count--leased { color: var(--m-accent, #4F46E5); }
.crawl-count--done   { color: var(--m-status-success, #16a34a); }
.crawl-count--failed { color: var(--m-status-danger, #dc2626); }
.job-st { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; }
.job-st--queued { color: var(--m-status-warning, #b45309); }
.job-st--leased { color: var(--m-accent, #4F46E5); }
.job-st--done   { color: var(--m-status-success, #16a34a); }
.job-st--failed { color: var(--m-status-danger, #dc2626); }
.sched-badge { font-size: 0.72rem; font-weight: 700; padding: 1px 7px; border-radius: 999px; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent, #4F46E5); }
.sched-next { display: block; font-size: 0.68rem; color: var(--m-text-tertiary, #94A3B8); margin-top: 1px; }
.reuse-prompt { border-left: 3px solid var(--m-accent, #4F46E5); }
.cov-progress { font-size: 0.74rem; font-weight: 600; color: var(--m-text-secondary, #475569); }
.cov-badge { font-size: 0.66rem; font-weight: 700; padding: 1px 6px; border-radius: 999px; background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent, #4F46E5); }
.cov-badge--done { background: #dcfce7; color: var(--m-status-success, #16a34a); }
.coverage-box { border-top: 1px dashed var(--m-border, #E2E8F0); padding-top: 10px; }
.coverage-result { border: 1px solid var(--m-border, #E2E8F0); border-left-width: 4px; border-radius: 6px; padding: 8px 12px; max-width: 560px; }
.coverage-result--yes { border-left-color: var(--m-status-success, #16a34a); background: var(--m-accent-soft, #f0fdf4); }
.coverage-result--no  { border-left-color: var(--m-status-warning, #b45309); background: #fffbeb; }
.coverage-verdict { font-weight: 700; font-size: 0.86rem; margin-bottom: 4px; }
.coverage-result--yes .coverage-verdict { color: var(--m-status-success, #16a34a); }
.coverage-result--no  .coverage-verdict { color: var(--m-status-warning, #b45309); }
.coverage-stats { display: flex; flex-wrap: wrap; gap: 14px; margin-bottom: 4px; }
.coverage-reason { line-height: 1.4; }

/* ---- typed crawl-source builder ---- */
.csrc-tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
.csrc-tile {
  display: flex; flex-direction: column; align-items: flex-start; gap: 4px; text-align: left;
  border: 1.5px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px);
  padding: 12px 13px; background: var(--m-bg-card, #fff); cursor: pointer; transition: all .12s;
}
.csrc-tile:hover { border-color: var(--m-border-strong, #C7CEDC); }
.csrc-tile--on {
  border-color: var(--m-accent, #4F46E5); background: var(--m-accent-soft, #EEF0FF);
  box-shadow: 0 0 0 3px var(--m-accent-soft, #E4E7FF);
}
/* Locked tile — plan-gated crawl type (honest upsell, non-selectable). */
.csrc-tile--locked {
  cursor: not-allowed; opacity: .72; background: var(--m-bg-content, #F8FAFC);
  border-style: dashed; border-color: var(--m-border, #E2E8F0);
}
.csrc-tile--locked:hover { border-color: var(--m-border, #E2E8F0); }
.csrc-tile--locked .csrc-tile__icon { color: var(--m-text-tertiary, #94A3B8); }
.csrc-tile--locked .csrc-tile__name { color: var(--m-text-secondary, #475569); }
.csrc-tile__lock {
  display: inline-flex; align-items: center; font-size: .68rem; font-weight: 700;
  color: var(--m-accent, #4F46E5); background: var(--m-accent-soft, #EEF0FF);
  border-radius: 999px; padding: 1px 8px; line-height: 1.5;
}
.csrc-tile__icon { font-size: 1.05rem; color: var(--m-accent, #4F46E5); }
.csrc-tile__name { font-size: .83rem; font-weight: 700; color: var(--m-text-primary, #1A1C23); }
.csrc-tile__desc { font-size: .7rem; color: var(--m-text-tertiary, #94A3B8); line-height: 1.3; }

.csrc-typebadge {
  display: flex; align-items: center; gap: 8px; font-size: .85rem; font-weight: 600;
  color: var(--m-accent, #4F46E5); background: var(--m-accent-soft, #EEF0FF);
  border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px);
  padding: 8px 12px; width: fit-content;
}

.csrc-field { margin-top: 4px; }
.csrc-slider { display: flex; align-items: center; gap: 12px; }
.csrc-slider .form-range { flex: 1; }
.csrc-slider__val {
  font-size: .8rem; font-weight: 700; font-family: var(--m-font-mono, monospace);
  color: var(--m-accent, #4F46E5); background: var(--m-accent-soft, #EEF0FF);
  border-radius: 6px; padding: 2px 9px; flex: none;
}

.csrc-chips { display: flex; flex-wrap: wrap; gap: 7px; }
.csrc-chip {
  font-size: .78rem; font-weight: 600; border-radius: 999px; padding: 4px 12px; cursor: pointer;
  border: 1px solid var(--m-border, #E2E8F0); color: var(--m-text-secondary, #475569); background: var(--m-bg-card, #fff);
}
.csrc-chip:hover { border-color: var(--m-border-strong, #C7CEDC); }
.csrc-chip--on { background: var(--m-accent, #4F46E5); border-color: var(--m-accent, #4F46E5); color: #fff; }

/* CN-1 geo_osm — per-category radius editor grid */
.csrc-radii { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; }
.csrc-radius { display: flex; align-items: center; gap: 8px; }
.csrc-radius__label { font-size: .74rem; font-weight: 600; color: var(--m-text-secondary, #64748B); min-width: 72px; text-transform: capitalize; }
.csrc-radius input { max-width: 80px; }

.csrc-adv { border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 8px); background: var(--m-bg-content, #F8FAFC); }
.csrc-adv summary { cursor: pointer; font-size: .78rem; font-weight: 600; color: var(--m-text-secondary, #475569); padding: 9px 13px; list-style: none; }
.csrc-adv summary::-webkit-details-marker { display: none; }
.csrc-adv__body { padding: 4px 13px 13px; }
.csrc-adv__json { font-size: .76rem; }
</style>
